import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import SignaturePad, { Options } from 'signature_pad';

import { ButtonModule } from '../button';

interface CustomEvent extends Event {
  detail: MouseEvent | Touch;
}

export interface SignaturePadOptions extends Options {
  placeholder?: string;
  canvasHeight?: number;
  canvasWidth?: number;
}

@Component({
    selector: 'lui-signature-pad',
    imports: [CommonModule, ButtonModule, MatIconModule],
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignaturePadComponent implements AfterViewInit {
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  @Input() options: SignaturePadOptions = {};
  @Input() readonly!: boolean;

  @Output() drawStart = new EventEmitter<MouseEvent | Touch>();
  @Output() drawBeforeUpdate = new EventEmitter<MouseEvent | Touch>();
  @Output() drawAfterUpdate = new EventEmitter<MouseEvent | Touch>();
  @Output() drawEnd = new EventEmitter<MouseEvent | Touch>();
  @Output() cleared = new EventEmitter<void>();

  private signaturePad!: SignaturePad;

  get isEmpty(): boolean {
    return this.signaturePad ? this.signaturePad.isEmpty() : true;
  }

  constructor(private cd: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    const canvas: HTMLCanvasElement = this.canvas.nativeElement;
    if (this.options.canvasHeight) {
      canvas.height = this.options.canvasHeight;
    }

    if (this.options.canvasWidth) {
      canvas.width = this.options.canvasWidth;
    }

    this.signaturePad = new SignaturePad(canvas, this.options);

    this.signaturePad.addEventListener('beginStroke', (event) => {
      if (this.isCustomEvent(event)) {
        this.beginStroke(event.detail);
      }
    });
    this.signaturePad.addEventListener('beforeUpdateStroke', (event) => {
      if (this.isCustomEvent(event)) {
        this.beforeUpdateStroke(event.detail);
      }
    });
    this.signaturePad.addEventListener('afterUpdateStroke', (event) => {
      if (this.isCustomEvent(event)) {
        this.afterUpdateStroke(event.detail);
      }
    });
    this.signaturePad.addEventListener('endStroke', (event) => {
      if (this.isCustomEvent(event)) {
        this.endStroke(event.detail);
      }
    });
  }

  fromDataUrl(
    dataURL: string,
    options: { ratio?: number; width?: number; height?: number } = {}
  ) {
    // set default height and width on read data from URL
    if (!('height' in options) && this.options.canvasHeight) {
      options.height = this.options.canvasHeight;
    }
    if (!('width' in options) && this.options.canvasWidth) {
      options.width = this.options.canvasWidth;
    }
    this.signaturePad.fromDataURL(dataURL, options).then(() => {
      this.cd.markForCheck();
    });
  }

  /**
   * Returns signature image as data URL (see https://mdn.io/todataurl for the list of possible parameters)
   */
  toDataUrl(imageType?: string, quality?: number) {
    return this.signaturePad.toDataURL(imageType, quality);
  }

  clear() {
    this.signaturePad.clear();
    this.cleared.emit();
  }

  private beginStroke(event: MouseEvent | Touch): void {
    this.drawStart.emit(event);
  }

  private beforeUpdateStroke(event: MouseEvent | Touch): void {
    this.drawBeforeUpdate.emit(event);
  }

  private afterUpdateStroke(event: MouseEvent | Touch): void {
    this.drawAfterUpdate.emit(event);
  }

  private endStroke(event: MouseEvent | Touch): void {
    this.drawEnd.emit(event);
    this.cd.markForCheck();
  }

  private isCustomEvent(event: Event): event is CustomEvent {
    return 'detail' in event && event.detail instanceof MouseEvent;
  }
}
